import { Download, Clock, Play, ArrowUpRight, BookOpen, GraduationCap, Award } from 'lucide-react';
import { Link } from 'react-router-dom';
import SectionMarker from '@/components/sections/SectionMarker';
import { PHOTOS, MEDIA_DOWNLOADS, DOCUMENTARY, PUBLICATIONS, BOOK, COURSES, CEO_INTELLIGENCE, AI_CERTIFICATIONS } from '@/constants/content';
import { MEDIA } from '@/constants/testIds';
import { usePageMeta } from '@/lib/usePageMeta';
import { SEO } from '@/constants/seo';

export default function Media() {
  usePageMeta({ ...SEO.media });
  return (
    <div className="bg-ivory">
      {/* HERO */}
      <section className="pt-40 lg:pt-56 pb-16 lg:pb-24">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-20 grid lg:grid-cols-12 gap-16 items-end">
          <div className="lg:col-span-7">
            <div className="eyebrow text-taupe mb-8">Media · The Public Record</div>
            <h1 className="font-serif-display text-6xl md:text-7xl lg:text-[8rem] leading-[0.94] tracking-tightest text-matte">
              On record.
              <br />
              <em className="italic text-taupe">On the page.</em>
            </h1>
          </div>
          <div className="lg:col-span-5">
            <p className="text-matte/70 leading-[1.85] text-[15px] max-w-md">
              Documentary, keynote presentations, magazine features, published works, and educational
              courses, the public credibility record.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 1 · FEATURED DOCUMENTARY */}
      <section className="py-16 lg:py-24 bg-matte text-ivory relative overflow-hidden" data-testid="media-documentary">
        <div className="absolute inset-0 opacity-25 noise-grain pointer-events-none" aria-hidden />
        <div className="relative mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-20 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-5">
            <div className="eyebrow text-gold mb-8">01 · Featured Documentary</div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tightest leading-[1.02]">
              {DOCUMENTARY.title}
            </h2>
            <div className="mt-4 font-serif-display italic text-2xl text-gold">{DOCUMENTARY.subtitle}</div>
            <p className="mt-10 text-ivory/75 leading-[1.85] max-w-lg">{DOCUMENTARY.description}</p>
            <div className="mt-10">
              <a
                href={DOCUMENTARY.url}
                target="_blank"
                rel="noreferrer"
                data-testid="media-cta-watch-documentary"
                className="btn-primary bg-gold text-matte hover:bg-[#b8944e]"
              >
                <Play size={14} strokeWidth={1.6} />
                Watch Documentary
                <ArrowUpRight size={14} strokeWidth={1.6} />
              </a>
            </div>
          </div>
          <div className="lg:col-span-7">
            <VideoPreview src={DOCUMENTARY.thumbnail} fallback={DOCUMENTARY.thumbnailFallback} href={DOCUMENTARY.url} label="Watch the documentary" testId="media-documentary-preview" />
          </div>
        </div>
      </section>

      {/* SECTION 2 · FEATURED PRESENTATION */}
      <section className="py-16 lg:py-24 bg-white" data-testid="media-presentation">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-20 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <VideoPreview src={CEO_INTELLIGENCE.summit.thumbnail} fallback={CEO_INTELLIGENCE.summit.thumbnailFallback} href={CEO_INTELLIGENCE.summit.url} label="Watch the summit" testId="media-presentation-preview" light />
          </div>
          <div className="lg:col-span-5 order-1 lg:order-2">
            <div className="eyebrow text-taupe mb-8">02 · Featured Presentation</div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tightest leading-[1.02] text-matte">
              CEO Intelligence
            </h2>
            <div className="mt-3 font-serif-display italic text-2xl text-taupe">
              How to Build a Business in 1 Hour with AI
            </div>
            <div className="mt-4 font-label text-[11px] tracking-widest uppercase text-matte/60">
              AI in Action Virtual Summit
            </div>
            <p className="mt-10 text-matte/70 leading-[1.85] max-w-lg">
              This presentation introduces the CEO Intelligence framework and demonstrates how AI
              can help aspiring entrepreneurs transform an idea into a business with greater speed,
              clarity, and confidence.
            </p>
            <div className="mt-10">
              <a href={CEO_INTELLIGENCE.summit.url} target="_blank" rel="noreferrer" data-testid="media-cta-watch-presentation" className="btn-primary">
                <Play size={14} strokeWidth={1.6} />
                Watch Presentation
                <ArrowUpRight size={14} strokeWidth={1.6} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3 · FEATURED PUBLICATIONS */}
      <section className="py-16 lg:py-24" data-testid="media-publications">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-20">
          <div className="grid lg:grid-cols-12 gap-8 mb-16">
            <div className="lg:col-span-5">
              <div className="eyebrow text-taupe mb-8">03 · Featured Publications</div>
              <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tightest leading-[1.02] text-matte">
                On the page, over the years.
              </h2>
            </div>
            <p className="lg:col-span-7 text-matte/70 leading-[1.85]">
              Over the years, my entrepreneurial journey has been featured across multiple
              publications documenting different seasons of my career, from launching businesses
              and building laboratories to becoming a software founder, speaker, and AI Business
              Transformation Strategist.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PUBLICATIONS.map((p, i) => (
              <a
                key={p.url}
                href={p.url}
                target="_blank"
                rel="noreferrer"
                data-testid={`media-publication-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                className="group bg-white rounded-2xl p-8 md:p-10 luxury-shadow border border-transparent hover:border-gold/40 transition-[transform,border-color] duration-500 hover:-translate-y-1 flex flex-col"
              >
                <div className="flex items-start justify-between mb-8">
                  <div className="eyebrow text-taupe">{String(i + 1).padStart(2, '0')}</div>
                  <div className="font-label text-[10px] tracking-widest uppercase text-matte/50">{p.year}</div>
                </div>
                <div className="font-serif-display italic text-3xl md:text-4xl tracking-tighter text-matte mb-6 group-hover:text-gold transition-colors duration-500">
                  {p.name}
                </div>
                <p className="text-[15px] text-matte/70 leading-[1.75] flex-1">{p.title}</p>
                <div className="mt-8 inline-flex items-center gap-2 font-label text-[10px] tracking-widest uppercase text-matte group-hover:text-gold transition-colors">
                  Read Interview
                  <ArrowUpRight size={14} strokeWidth={1.4} />
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4 · PUBLISHED WORKS */}
      <section className="py-16 lg:py-24 bg-sand/40" data-testid="media-book">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-20 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-5">
            <div className="eyebrow text-taupe mb-8">04 · Published Works</div>
            <h2 className="font-serif-display text-5xl md:text-6xl lg:text-7xl tracking-tightest leading-[0.95] text-matte">
              {BOOK.title}
            </h2>
            <p className="mt-4 font-serif-display italic text-2xl text-taupe leading-snug">{BOOK.subtitle}</p>
            <div className="mt-8 flex items-center gap-4">
              <span className="eyebrow text-taupe">Published</span>
              <span className="hairline-gold h-px w-8" />
              <span className="font-label text-[11px] tracking-widest uppercase text-matte">{BOOK.publishedDate}</span>
            </div>
            <p className="mt-10 text-matte/70 leading-[1.85] max-w-lg">{BOOK.description}</p>
            <div className="mt-10">
              <a href={BOOK.url} target="_blank" rel="noreferrer" data-testid="media-cta-buy-book" className="btn-primary">
                <BookOpen size={14} strokeWidth={1.6} />
                {BOOK.ctaLabel}
                <ArrowUpRight size={14} strokeWidth={1.6} />
              </a>
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="rounded-3xl bg-white p-8 lg:p-12 luxury-shadow flex items-center justify-center">
              <img
                src={BOOK.cover}
                alt="Faith Got Me Here book cover"
                className="w-full max-w-[360px] lg:max-w-[440px] h-auto rounded-md shadow-2xl"
                loading="lazy"
                data-testid="media-book-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 5 · COURSES & LEARNING */}
      <section className="py-16 lg:py-24" data-testid="media-courses">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-20">
          <div className="grid lg:grid-cols-12 gap-8 mb-16">
            <div className="lg:col-span-5">
              <div className="eyebrow text-taupe mb-8">05 · Courses & Learning</div>
              <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tightest leading-[1.02] text-matte">
                Learn from the desk.
              </h2>
            </div>
            <p className="lg:col-span-7 text-matte/70 leading-[1.85]">{COURSES.intro}</p>
          </div>

          <article className="bg-white rounded-3xl p-10 md:p-14 luxury-shadow border border-taupe/20 grid lg:grid-cols-12 gap-10 items-center" data-testid="media-course-featured">
            <div className="lg:col-span-5">
              <div className="w-16 h-16 rounded-full bg-ivory border border-gold/40 grid place-items-center mb-8">
                <GraduationCap size={22} strokeWidth={1.4} className="text-gold" />
              </div>
              <div className="eyebrow text-taupe mb-4">Featured Course</div>
              <h3 className="font-serif-display text-3xl md:text-4xl lg:text-5xl tracking-tightest leading-[1.05] text-matte">
                {COURSES.featured.title}
              </h3>
              <div className="mt-4 font-serif-display italic text-xl text-taupe">
                {COURSES.featured.platform} · Instructor: {COURSES.featured.instructor}
              </div>
            </div>
            <div className="lg:col-span-7">
              <p className="text-matte/70 leading-[1.85]">{COURSES.featured.description}</p>
              <div className="mt-10">
                <a href={COURSES.featured.url} target="_blank" rel="noreferrer" data-testid="media-cta-enroll-udemy" className="btn-primary">
                  {COURSES.featured.ctaLabel}
                  <ArrowUpRight size={14} strokeWidth={1.6} />
                </a>
              </div>
            </div>
          </article>

          <div className="mt-16">
            <div className="eyebrow text-taupe mb-6">Development</div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {COURSES.future.map((f, i) => {
                const isExternal = typeof f.url === 'string' && f.url.startsWith('http');
                const isComingSoon = f.comingSoon;
                const content = (
                  <>
                    <span className="font-label text-[10px] tracking-widest uppercase text-taupe pt-1">{String(i + 1).padStart(2, '0')}</span>
                    <div className="flex-1">
                      <span className="font-serif-display text-lg text-matte leading-tight tracking-tight block">{f.name}</span>
                      {isComingSoon && (
                        <span className="mt-1 inline-block font-label text-[9px] tracking-[0.28em] uppercase text-taupe/70">Coming Soon</span>
                      )}
                    </div>
                    {!isComingSoon && (
                      <ArrowUpRight size={14} strokeWidth={1.4} className="text-matte/40 group-hover:text-gold transition-colors mt-1" />
                    )}
                  </>
                );
                const base = 'group bg-ivory rounded-2xl p-6 border border-taupe/20 hover:border-gold/40 transition-colors flex items-start gap-3';
                if (isComingSoon) {
                  return (
                    <div key={f.name} className={`${base} opacity-70 cursor-not-allowed`}>{content}</div>
                  );
                }
                if (isExternal) {
                  return (
                    <a key={f.name} href={f.url} target="_blank" rel="noreferrer" className={base}>{content}</a>
                  );
                }
                return (
                  <Link key={f.name} to={f.url} className={base}>{content}</Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 6 · AI CERTIFICATIONS */}
      <section className="py-16 lg:py-24 bg-white" data-testid="media-ai-certifications">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-20">
          <div className="grid lg:grid-cols-12 gap-8 mb-14">
            <div className="lg:col-span-5">
              <div className="eyebrow text-taupe mb-8">06 · AI Certifications</div>
              <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tightest leading-[1.02] text-matte">
                Verified <em className="italic text-taupe">AI credentials.</em>
              </h2>
            </div>
            <p className="lg:col-span-7 text-matte/70 leading-[1.85]">
              A professional foundation in artificial intelligence, spanning systems, governance,
              business integration, and marketing. Each credential is independently accredited
              and verifiable through CredsVerse.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {AI_CERTIFICATIONS.map((c, i) => (
              <a
                key={c.title}
                href={c.url}
                target="_blank"
                rel="noreferrer"
                data-testid={`media-certification-${i}`}
                className="group relative bg-ivory rounded-2xl p-8 md:p-10 border border-taupe/25 hover:border-gold/60 hover:bg-white transition-[border-color,background-color,transform] duration-500 hover:-translate-y-1"
              >
                <div className="flex items-start justify-between mb-8">
                  <div className="w-12 h-12 rounded-full border border-gold/50 grid place-items-center">
                    <Award size={18} strokeWidth={1.4} className="text-gold" />
                  </div>
                  <div className="font-label text-[10px] tracking-[0.32em] uppercase text-gold">{c.level}</div>
                </div>
                <div className="font-label text-[10px] tracking-[0.28em] uppercase text-taupe mb-3">Verified Credential</div>
                <h3 className="font-serif-display text-2xl md:text-3xl leading-tight tracking-tighter text-matte mb-6 group-hover:text-gold transition-colors duration-500">
                  {c.title}
                </h3>
                <div className="hairline-gold h-px w-10 mb-6" aria-hidden />
                <dl className="space-y-3 text-[13px] leading-relaxed">
                  <div>
                    <dt className="font-label text-[9px] tracking-[0.28em] uppercase text-taupe mb-1">Accredited By</dt>
                    <dd className="text-matte/80">{c.accreditor}</dd>
                  </div>
                  <div>
                    <dt className="font-label text-[9px] tracking-[0.28em] uppercase text-taupe mb-1">Accreditation</dt>
                    <dd className="text-matte/80">{c.board}</dd>
                  </div>
                  <div>
                    <dt className="font-label text-[9px] tracking-[0.28em] uppercase text-taupe mb-1">Program</dt>
                    <dd className="text-matte/80 font-mono text-[12px]">{c.program}</dd>
                  </div>
                </dl>
                <div className="mt-8 inline-flex items-center gap-2 font-label text-[10px] tracking-[0.28em] uppercase text-matte group-hover:text-gold transition-colors">
                  Verify Credential
                  <ArrowUpRight size={14} strokeWidth={1.4} />
                </div>
              </a>
            ))}
          </div>

          <div className="mt-12 flex justify-center">
            <Link to="/credentials" className="btn-ghost" data-testid="media-cta-credentials">
              View Full Credentials Page
              <ArrowUpRight size={14} strokeWidth={1.6} />
            </Link>
          </div>
        </div>
      </section>

      {/* BRAND ASSETS */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-20">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
            <div>
              <div className="eyebrow text-taupe mb-8">07 · Downloads</div>
              <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tightest leading-[0.95] text-matte">
                Brand & press assets.
              </h2>
            </div>
            <p className="text-matte/70 max-w-md">
              Final assets are in production. Each item is architected to accept its file without
              redesigning this page.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {MEDIA_DOWNLOADS.map((d, i) => (
              <div
                key={d.key}
                data-testid={MEDIA.downloadCard(d.key)}
                className="group relative bg-ivory rounded-2xl p-8 border border-taupe/20 hover:border-gold/40 transition-colors duration-500"
              >
                <div className="flex items-start justify-between mb-8">
                  <div className="eyebrow text-taupe">{String(i + 1).padStart(2, '0')}</div>
                  <div className="inline-flex items-center gap-2 text-taupe">
                    <Clock size={14} strokeWidth={1.4} />
                    <span className="font-label text-[10px] tracking-widest uppercase">Coming Soon</span>
                  </div>
                </div>
                <h3 className="font-serif-display text-2xl md:text-3xl tracking-tighter text-matte leading-tight mb-3">
                  {d.label}
                </h3>
                <p className="text-sm text-matte/60">{d.hint}</p>
                <button type="button" disabled className="mt-8 inline-flex items-center gap-2 text-matte/40 font-label text-[10px] tracking-widest uppercase cursor-not-allowed">
                  <Download size={14} strokeWidth={1.4} />
                  Download · Available soon
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function VideoPreview({ src, fallback, href, label, testId, light = false }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      aria-label={label}
      data-testid={testId}
      className="group relative block rounded-3xl overflow-hidden luxury-shadow"
    >
      <img
        src={src}
        onError={(e) => { e.currentTarget.src = fallback; }}
        alt={label}
        className="w-full aspect-video object-cover transition-transform duration-700 group-hover:scale-[1.02]"
        loading="lazy"
      />
      <div className={`absolute inset-0 pointer-events-none bg-gradient-to-t ${light ? 'from-matte/40' : 'from-matte/70'} via-transparent to-transparent`} aria-hidden />
      <div className="absolute inset-0 grid place-items-center">
        <span className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-gold grid place-items-center transition-transform duration-500 group-hover:scale-110 shadow-[0_20px_60px_-15px_rgba(200,161,90,0.7)]">
          <Play size={30} strokeWidth={1.4} className="text-matte translate-x-0.5" />
        </span>
      </div>
    </a>
  );
}
